import { useState, useCallback, useRef } from 'react';

/**
 * Префикс для всех ключей состояния таблиц в sessionStorage.
 * Состояние живёт в рамках вкладки: переживает переходы между страницами
 * и перезагрузку (F5), но сбрасывается при закрытии вкладки/браузера.
 */
const STORAGE_PREFIX = 'mc-admin:table-state:';

function readFromSession<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') {
    return fallback;
  }
  try {
    const raw = window.sessionStorage.getItem(STORAGE_PREFIX + key);
    if (raw === null) {
      return fallback;
    }
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeToSession<T>(key: string, value: T): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
  } catch {
    // sessionStorage может быть недоступен (privacy mode, quota) — тихо игнорируем
  }
}

type SetStateAction<T> = T | ((prev: T) => T);
type Dispatch<T> = (value: SetStateAction<T>) => void;

function isUpdaterFn<T>(value: SetStateAction<T>): value is (prev: T) => T {
  return typeof value === 'function';
}

/**
 * Drop-in замена `useState`, синхронизирующая значение с sessionStorage по
 * стабильному ключу. Используется для сохранения состояния таблиц админки
 * (страница пагинации, поиск, сортировка, фильтры) между навигациями.
 *
 * @param key   Уникальный ключ в неймспейсе ресурса, напр. `users:page`.
 * @param initialValue Значение по умолчанию, если в хранилище ничего нет.
 *
 * @example
 * const [page, setPage] = usePersistedState('users:page', 1);
 */
export function usePersistedState<T>(
  key: string,
  initialValue: T,
): [T, Dispatch<T>] {
  // Ключ фиксируется при монтировании — смена ключа на лету не поддерживается
  // (как и у useState нет реакции на смену initialValue).
  const keyRef = useRef(key);

  const [state, setState] = useState<T>(() => readFromSession(key, initialValue));

  const setPersistedState = useCallback<Dispatch<T>>((value) => {
    setState((prev) => {
      const next = isUpdaterFn(value) ? value(prev) : value;
      writeToSession(keyRef.current, next);
      return next;
    });
  }, []);

  return [state, setPersistedState];
}
